/**
 * Amazon Connect App SDK utilities
 */

import { AmazonConnectApp, AppContactScope } from '@amazon-connect/app';
import { ContactClient } from '@amazon-connect/contact';

let connectProvider: any = null;
let contactClient: ContactClient | null = null;
let isFullyInitialized = false;
let personId: string | null = null;
let contactId: string | null = null;

/**
 * Initialize Amazon Connect App SDK
 */
export const initializeConnectSdk = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const { provider } = AmazonConnectApp.init({
        onCreate: async (event) => {
          const { appInstanceId } = event.context;
          console.log('Connect app initialized:', appInstanceId);
          connectProvider = provider;
          resolve();
        },
        onDestroy: async (event) => {
          console.log('Connect app being destroyed');
          connectProvider = null;
          isFullyInitialized = false;
          personId = null;
          contactId = null;
        },
      });
    } catch (error) {
      console.error('Failed to initialize Connect App SDK:', error);
      reject(error);
    }
  });
};

/**
 *  Helper function since contact attributes can come in may case stylings.
 * @param obj 
 * @param targetKey 
 * @returns 
 */
export const findByKeyIgnoreCase = (obj: any, targetKey: string) => {
  const key = Object.keys(obj).find(k => k.toLowerCase() === targetKey.toLowerCase());
  return key ? obj[key]?.value || null : null;
};
/**
 * Manually check for contact attributes and update personID/contactID
 */
export const checkContactAttributes = async (): Promise<boolean> => {
  const client = getContactClient();
  if (!client) {
    console.log('Contact client not available');
    return false;
  }

  try {
    const attributes = await client.getAttributes(AppContactScope.CurrentContactId, '*') as any;
    
    if (attributes) {
      // Extract PersonID from nested structure, it comes in all forms, so check multiple variations.
      personId = findByKeyIgnoreCase(attributes, 'personid') || null;
      
      // Use CurrentContactId for contactId
      contactId = findByKeyIgnoreCase(attributes, 'currentContactId') || null;
      
      console.log('Contact attributes retrieved:', { personId, contactId });
      isFullyInitialized = !!(personId && contactId);
      return isFullyInitialized;
    }
  } catch (error) {
    console.log('Failed to get contact attributes:', error);
  }
  
  return false;
};

/**
 * Get Connect provider instance
 */
export const getConnectProvider = () => {
  return connectProvider;
};

/**
 * Get contact client instance
 */
export const getContactClient = () => {
  if (!contactClient && connectProvider) {
    contactClient = new ContactClient(connectProvider);
  }
  return contactClient;
};

/**
 * Check if Connect is fully initialized with contact data
 */
export const isConnectFullyInitialized = (): boolean => {
  return isFullyInitialized;
};

/**
 * Get current initialization status and try to update contact data
 */
export const getConnectStatus = async (): Promise<{ initialized: boolean; personId: string | null; contactId: string | null }> => {
  await checkContactAttributes();
  return {
    initialized: isFullyInitialized,
    personId,
    contactId
  };
};

/**
 * Get person ID from contact attributes
 */
export const getPersonId = (): string | null => {
  return personId;
};

/**
 * Get contact ID from contact attributes
 */
export const getContactId = (): string | null => {
  return contactId;
};



/**
 * Dump all available contact information for testing
 */
export const dumpContactInfo = async () => {
  console.log('=== CONNECT DEBUG INFO ===');
  console.log('Provider available:', !!connectProvider);
  
  const client = getContactClient();
  if (!client) {
    console.log('❌ Contact client not available');
    return;
  }
  console.log('✓ Contact client created');

  try {
    const contactInfo: any = {};
    
    // Check if there's an active contact first
    console.log('\n--- Testing Contact APIs ---');
    console.log('Note: You need an active contact (incoming/outgoing call) to test these APIs');
    
    try {
      contactInfo.queue = await client.getQueue(AppContactScope.CurrentContactId);
      console.log('✓ Queue access granted:', contactInfo.queue);
    } catch (e: any) {
      console.log('✗ Queue access failed:', e.message || e);
      if (e.message?.includes('requestNotAuthorized')) {
        console.log('  → This usually means no active contact');
      }
    }

    try {
      contactInfo.specificAttributes = await client.getAttributes(AppContactScope.CurrentContactId, '*');
      console.log('✓ Specific attributes:', contactInfo.specificAttributes);
    } catch (e: any) {
      console.log('✗ Specific attributes failed:', e.message || e);
    } 
    console.log('\n=== CONTACT INFORMATION DUMP ===');
    console.log(JSON.stringify(contactInfo, null, 2));
    
    return contactInfo;
  } catch (error) {
    console.error('Failed to dump contact info:', error);
  }
};

/**
 * Dump all available Connect information
 */
export const dumpAllConnectInfo = async () => {
  console.log('\n🔍 DUMPING ALL CONNECT INFORMATION\n');
  console.log('Provider available:', !!connectProvider);
  console.log('Contact client available:', !!getContactClient());
  
  await dumpContactInfo();
};